import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { EmptyState } from '@components/empty-state';
import { IconIntegration } from '@components/icons/integration';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { Spinner } from '@components/loading-spinner';
import { Modal, useModal } from '@components/modal';
import { forceHideModal } from '@components/modal/modal';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useEditPollingVisibility, useListPollingEndpoints } from 'api/integrations/hooks';
import { usePollingTestRequest } from 'api/journey-polling/hooks';
import { routePaths } from 'ui/authenticated/navigation';
import { IntegrationThumbnail } from 'ui/authenticated/settings/pages/organization/lists/sync-list-sidebar';

export const useEndpointVisibilityModal = () => useModal(EndpointVisibilityModal);

export const EndpointVisibilityModal = ({ journeyShortId }: { journeyShortId: string }) => {
  const { t } = useScopedTranslate(locale);
  const editVisibilityMutation = useEditPollingVisibility();
  const testRequestMutation = usePollingTestRequest(journeyShortId);
  const integrationsQuery = useListPollingEndpoints(journeyShortId);
  const integrations = useMemo(() => integrationsQuery.data ?? [], [integrationsQuery.data]);

  const initialState = useMemo(
    () =>
      Object.fromEntries(
        integrations.flatMap((i) => i.endpoints.map((e) => [e.shortId, e.showInPolling ?? false]))
      ),
    [integrations]
  );

  const [selected, setSelected] = useState<Record<string, boolean>>(initialState);
  const hasChanges = useMemo(() => {
    const currentKeys = Object.keys(selected);
    const initialKeys = Object.keys(initialState);

    if (currentKeys.length !== initialKeys.length) {
      return true;
    }
    return currentKeys.some((key) => selected[key] !== initialState[key]);
  }, [selected, initialState]);

  const toggle = (endpointId: string) => {
    setSelected((prev) => ({ ...prev, [endpointId]: !prev[endpointId] }));
  };

  const handleSave = () => {
    const selectedIds = Object.entries(selected)
      .filter(([_, isChecked]) => isChecked)
      .map(([endpointId]) => endpointId);
    const data = { visibleIds: selectedIds };
    editVisibilityMutation.mutateAsync(data, {
      onSuccess: forceHideModal,
    });
  };

  if (integrationsQuery.isLoading) {
    return <Spinner />;
  }

  const hasIntegrations = integrations.length > 0;

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Stack space={6}>
          <Text text={t('selectEndpoint')} font="regular14" color="textSecondary" />
          <Stack space={2}>
            {hasIntegrations ? (
              <>
                <StyledScrollDiv>
                  {integrations.map((integration) => (
                    <Stack space={2} key={integration.shortId} style={{ marginBottom: 20 }}>
                      <Stack space={2} orientation="horizontal" align="center">
                        <IntegrationThumbnail thumbnail={integration.thumbnail} />
                        <Text
                          text={integration.name}
                          font="semibold14"
                          style={lineClamp('-webkit-inline-box', 1, true)}
                        />
                      </Stack>
                      {integration.endpoints.map((endpoint) => {
                        const isInvalidEndpoint = endpoint.isValidPollingEndpoint === false;
                        return (
                          <Stack
                            space={2}
                            orientation="horizontal"
                            justify="space-between"
                            key={endpoint.shortId}
                          >
                            <Stack space={2} orientation="horizontal" align="center">
                              <Checkbox
                                labelVisible={false}
                                label={endpoint.name}
                                defaultValue={endpoint.showInPolling ?? false}
                                value={selected[endpoint.shortId]}
                                onChange={() => toggle(endpoint.shortId)}
                                isDisabled={isInvalidEndpoint}
                              />
                              <Text
                                text={endpoint.name}
                                font="regular14"
                                color={isInvalidEndpoint ? 'textSecondary' : 'textPrimary'}
                                style={lineClamp('-webkit-inline-box', 1, true)}
                              />
                              {isInvalidEndpoint && <Text text={'*'} font="regular14" />}
                              <Tag
                                text={endpoint.method}
                                variant={endpoint.method === 'GET' ? 'green' : 'blue'}
                                size="small"
                              />
                            </Stack>
                            {endpoint.isValidPollingEndpoint === false && (
                              <ValidateButton
                                text={t('validate')}
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  testRequestMutation.mutate({
                                    integrationShortId: integration.shortId,
                                    endpointShortId: endpoint.shortId,
                                  });
                                }}
                              />
                            )}
                          </Stack>
                        );
                      })}
                    </Stack>
                  ))}
                </StyledScrollDiv>
                <Text text={t('invalidEndpointsText')} font="regular14" color="textHelper" />
              </>
            ) : (
              <EmptyState
                icon={<IconIntegration size={40} />}
                variant="small"
                header={t('emptyHeader')}
                content={t('emptyContent')}
                action={
                  <Stack space={2}>
                    <Button
                      text={t('integrationsPage')}
                      iconRight={<IconOpenNewTab size={16} color="buttonIconNegative" />}
                      size="sm"
                      href={routePaths.integrations}
                      target="_blank"
                    />
                  </Stack>
                }
                skipMargin
              />
            )}
          </Stack>
        </Stack>
      </Modal.Content>
      {hasIntegrations && (
        <Modal.Footer>
          <Stack space={2} orientation="horizontal" justify="flex-end">
            <Button variant="secondary" onClick={forceHideModal} text={t('core.cancel')} />
            <Button
              variant="primary-blue"
              onClick={handleSave}
              text={t('core.save')}
              isDisabled={editVisibilityMutation.isLoading || !hasChanges}
            />
          </Stack>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export const StyledScrollDiv = styled.div({
  backgroundColor: theme.color.uiTrans5,
  padding: `${pxToRem(8)} ${pxToRem(16)}`,
  maxHeight: pxToRem(484),
  minHeight: pxToRem(200),
  overflowY: 'auto',
  borderRadius: pxToRem(8),
  scrollbarColor: `${theme.color.uiTrans10} transparent`,
});

const ValidateButton = styled(Button)({
  padding: 0,
});

export const locale = {
  en: {
    title: 'Endpoint visibility settings',
    selectEndpoint: 'Select endpoints to display',
    invalidEndpointsText: '* Unvalidated endpoints are disabled',
    validate: 'Validate',
    emptyHeader: 'No integrations',
    emptyContent:
      'You do not have any integrations, add and manage your integrations from the integrations page',
    integrationsPage: 'Integrations page',
  },
};
